import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRotateRight,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import Title from "../shared/components/Title";
import FilterBar from "../features/performance-report/components/FilterBar";
import KpiStrip from "../features/performance-report/components/KpiStrip";
import PeriodTabs from "../features/performance-report/components/PeriodTabs";
import PerformanceTable from "../features/performance-report/components/PerformanceTable";
import apiClient from "../utils/apiClient";

const API = "/api/v1/performanceReport";

async function fetchJson(path) {
  const response = await apiClient.get(path);
  if (response.ok) return response.json();

  // 403 means the signed-in user lacks the performance-report module. That is a
  // permissions problem to raise with IT, not something retrying will fix.
  if (response.status === 403) {
    throw new Error(
      "You do not have access to the Performance Report. Ask IT to add you to a role that grants it.",
    );
  }

  const body = await response.json().catch(() => null);
  throw new Error(body?.detail || `Request failed (${response.status})`);
}

const REQUEST_FIELDS = [
  "week_end_date",
  "billing_company",
  "account_manager",
  "referral_portfolio",
];

/** One request covers every period, so the cache key omits the period. */
const reportKey = (request) => REQUEST_FIELDS.map((field) => request[field] ?? "").join("|");

function Skeleton() {
  return (
    <div className="space-y-4" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading performance data</span>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {[0, 1, 2].map((index) => (
          <div
            key={index}
            className="h-32 animate-pulse rounded-2xl border border-gray-200 bg-white motion-reduce:animate-none"
          >
            <div className="space-y-3 p-5">
              <div className="h-2.5 w-20 rounded bg-gray-100" />
              <div className="h-6 w-28 rounded bg-gray-100" />
              <div className="h-1.5 w-full rounded bg-gray-100" />
            </div>
          </div>
        ))}
      </div>
      <div className="h-96 animate-pulse rounded-2xl border border-gray-200 bg-white motion-reduce:animate-none" />
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-6" role="alert">
      <div className="flex items-start gap-3">
        <FontAwesomeIcon
          icon={faTriangleExclamation}
          className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500"
        />
        <div className="flex-1">
          <p className="text-sm font-semibold text-red-700">
            Could not load the performance report
          </p>
          <p className="mt-1 text-xs text-red-600">{message}</p>
          <button
            type="button"
            onClick={onRetry}
            className="mt-3 flex cursor-pointer items-center gap-1.5 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-xs font-semibold text-red-600 transition-colors duration-200 hover:bg-red-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
          >
            <FontAwesomeIcon icon={faRotateRight} className="h-3 w-3" />
            Try again
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PerformanceReport() {
  const [periods, setPeriods] = useState([]);
  const [activePeriod, setActivePeriod] = useState(null);
  const [filters, setFilters] = useState(null);
  const [filterValues, setFilterValues] = useState({
    financial_year: "",
    week_end_date: "",
    billing_company: "",
    account_manager: "",
    referral_portfolio: "",
  });

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const cache = useRef(new Map());
  // Guards against a slow earlier request overwriting a newer one when filters
  // change quickly.
  const requestId = useRef(0);
  const [bootstrapNonce, setBootstrapNonce] = useState(0);

  /* Bootstrap: tab registry + filter options, and the default week to open on. */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [periodsResponse, filtersResponse] = await Promise.all([
          fetchJson(`${API}/periods`),
          fetchJson(`${API}/filters`),
        ]);
        if (cancelled) return;

        setPeriods(periodsResponse.periods ?? []);
        setActivePeriod((current) => current ?? periodsResponse.periods?.[0]?.key ?? null);
        setFilters(filtersResponse);
        const defaultWeek = filtersResponse.default_week_end_date ?? "";
        const defaultYear = (filtersResponse.week_ends ?? []).find(
          (week) => week.week_end_date === defaultWeek,
        )?.financial_year;
        setFilterValues((current) => ({
          ...current,
          financial_year: defaultYear ?? "",
          week_end_date: defaultWeek,
        }));
      } catch (caught) {
        if (!cancelled) {
          setError(caught.message || "Unable to reach the API.");
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [bootstrapNonce]);

  /* Report data — one call returns CW, MTD and YTD together. */
  const loadReport = useCallback(async (request) => {
    const key = reportKey(request);
    const cached = cache.current.get(key);
    if (cached) {
      setReport(cached);
      setLoading(false);
      setError(null);
      return;
    }

    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      REQUEST_FIELDS.forEach((field) => {
        if (request[field]) params.append(field, request[field]);
      });
      const data = await fetchJson(`${API}/report?${params}`);
      if (id !== requestId.current) return;
      cache.current.set(key, data);
      setReport(data);
    } catch (caught) {
      if (id !== requestId.current) return;
      setError(caught.message || "Unable to load the report.");
      setReport(null);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!filterValues.week_end_date) return;
    loadReport(filterValues);
  }, [filterValues, loadReport]);

  const handleFilterChange = (patch) =>
    setFilterValues((current) => ({ ...current, ...patch }));

  const retry = () => {
    cache.current.clear();
    if (filterValues.week_end_date) loadReport(filterValues);
    else setBootstrapNonce((nonce) => nonce + 1);
  };

  const activePeriodMeta = useMemo(
    () => periods.find((period) => period.key === activePeriod) ?? null,
    [periods, activePeriod],
  );

  // Switching tabs is a pure client-side pick from the single response, which is
  // what guarantees the three periods on screen are cut from the same data.
  const periodData = activePeriod ? report?.periods?.[activePeriod] : null;

  // Keep the previous table on screen while a new week loads, so changing filters
  // does not blank the page for someone comparing figures.
  const showSkeleton = loading && !report;

  return (
    <div className="min-h-screen bg-[#f0f5fb] p-6">
      <div className="mx-auto max-w-[1800px] space-y-5">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <Title title="Performance Report" />
            <p className="-mt-3 text-sm text-gray-500">
              {activePeriodMeta?.subtitle ?? "Actual vs Budget vs Prior Year"}
              {periodData?.period_label && (
                <span className="ml-2 font-semibold text-[#144677]">
                  · {periodData.period_label}
                </span>
              )}
              {periodData?.context?.weeks_in_period > 1 && (
                <span className="ml-2 text-gray-400">
                  ({periodData.context.weeks_in_period} weeks)
                </span>
              )}
            </p>
          </div>
          <PeriodTabs
            periods={periods}
            activeKey={activePeriod}
            onChange={setActivePeriod}
            disabled={!periods.length}
          />
        </div>

        <FilterBar
          filters={filters}
          values={filterValues}
          onChange={handleFilterChange}
          disabled={!filters}
        />

        {error && <ErrorState message={error} onRetry={retry} />}

        {showSkeleton && !error && <Skeleton />}

        {periodData && !error && (
          <div
            className={`space-y-5 transition-opacity duration-200 ${
              loading ? "pointer-events-none opacity-60" : "opacity-100"
            }`}
            aria-busy={loading}
          >
            <KpiStrip totals={periodData.totals} metrics={report.metrics} />
            <PerformanceTable
              rows={periodData.rows}
              totals={periodData.totals}
              metrics={report.metrics}
              periodShort={periodData.period?.short}
              periodLabel={periodData.period_label}
            />
          </div>
        )}

        {report && !periodData && !error && !loading && (
          <div className="rounded-2xl border border-gray-200 bg-white p-10 text-center shadow-sm">
            <p className="text-sm font-medium text-gray-500">
              No data for this period
            </p>
            <p className="mt-1 text-xs text-gray-400">
              Try a different billing week, or clear the billing company filter.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
